import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component that scrolls to top of page on route change
 * This component should be placed in App.js to work for all pages
 */
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Function to scroll to top using multiple methods
    const scrollToTop = () => {
      // Method 1: Direct assignment (most reliable)
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;
      }
      
      if (document.body) {
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
      }
      
      // Method 2: Window scrollTo (try both instant and smooth)
      if (window.scrollTo) {
        try {
          window.scrollTo(0, 0);
        } catch (e) {
          // Fallback
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
          });
        }
      }
      
      // Method 3: Try scrolling root element
      const root = document.getElementById('root');
      if (root && root.scrollTop !== undefined) {
        root.scrollTop = 0;
        root.scrollLeft = 0;
      }
      
      // Method 4: Try scrolling any scrollable containers
      const scrollableSelectors = [
        'main',
        '[role="main"]',
        '.main-content',
        '.content',
        '.page-content',
        '.overflow-y-auto',
        '.overflow-auto'
      ];
      
      scrollableSelectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            if (element && element.scrollTop !== undefined) {
              element.scrollTop = 0;
              element.scrollLeft = 0;
            }
          });
        } catch (e) {
          // Ignore errors
        }
      });
    };

    // Immediate scroll (synchronous)
    scrollToTop();
    
    // Also scroll after a tiny delay to catch any async rendering
    const timeoutId1 = setTimeout(scrollToTop, 0);
    
    // Scroll after requestAnimationFrame (next frame)
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
      // Also try one more time after a short delay
      setTimeout(scrollToTop, 10);
    });

    // Scroll after a slightly longer delay to catch late renders
    const timeoutId2 = setTimeout(scrollToTop, 100);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      cancelAnimationFrame(rafId);
    };
  }, [pathname, search]);

  return null;
};

export default ScrollToTop;

